<template>
  <el-dialog 
    title="用户角色分配" 
    :visible.sync="dialogVisible"
    width="950px"
    @open="init"
    @closed="handleClose">
    <div class="permission-content">
      <!-- 左侧用户列表 -->
      <div class="left-panel">
        <h3 class="panel-title">用户列表</h3>
        <div class="search-box">
          <el-input
            v-model="userSearchKeyword"
            placeholder="搜索用户"
            prefix-icon="el-icon-search"
            clearable
            @input="filterUsers">
          </el-input>
        </div>
        <el-table
          v-loading="userLoading"
          :data="filteredUserList"
          highlight-current-row
          @current-change="handleUserSelect"
          height="420"
          style="width: 100%">
          <el-table-column
            prop="username"
            label="用户名"
            min-width="150">
          </el-table-column>
          <el-table-column
            prop="realName"
            label="姓名"
            min-width="100">
          </el-table-column>
          <el-table-column
            width="30"
            align="center">
            <template slot-scope="scope">
              <i v-if="currentUser && currentUser.id === scope.row.id" 
                class="el-icon-arrow-right selection-indicator"></i>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <!-- 中间角色分配区域 -->
      <div class="middle-panel">
        <h3 class="panel-title">
          <span>角色分配</span>
          <span v-if="currentUser" class="current-selected">
            当前用户: <el-tag size="small">{{ currentUser.username }}</el-tag>
          </span>
        </h3>

        <div v-if="!currentUser" class="empty-notice">
          <i class="el-icon-warning-outline"></i>
          <p>请先在左侧选择用户</p>
        </div>

        <div v-else class="role-assignment-area">
          <el-divider content-position="left">可用角色</el-divider>
          <div class="role-selection">
            <el-checkbox-group v-model="assignedRoleCodes" @change="handleRoleChange">
              <el-checkbox 
                v-for="role in allRoleOptions" 
                :key="role.id" 
                :label="role.roleCode"
                class="role-checkbox">
                <div class="role-info">
                  <div class="role-name">{{ role.roleName }}</div>
                  <div v-if="role.description" class="role-desc">{{ role.description }}</div>
                </div>
              </el-checkbox>
            </el-checkbox-group>
          </div>

          <div class="action-buttons">
            <el-button 
              type="primary" 
              @click="saveUserRoles" 
              :loading="savingRoles"
              :disabled="!roleChanged">
              保存角色分配
            </el-button>
          </div>
        </div>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import { getRoleList } from '@/api/role'
import { getUserList, getUserRoles, assignUserRoles } from '@/api/user'

export default {
  name: 'UserRoleAssignmentDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      dialogVisible: this.visible,
      // 用户数据
      userList: [],
      filteredUserList: [],
      userLoading: false,
      userSearchKeyword: '',
      currentUser: null,

      // 角色数据
      roleList: [],
      roleLoading: false,
      allRoleOptions: [],
      assignedRoleCodes: [],
      originalAssignedRoleCodes: [],
      roleChanged: false, 
      savingRoles: false
    }
  },
  watch: {
    visible(val) {
      this.dialogVisible = val;
    },
    dialogVisible(val) {
      this.$emit('update:visible', val);
    }
  },
  methods: {
    init() {
      this.fetchUserList();
      this.fetchRoleList();
      this.userSearchKeyword = '';
      this.currentUser = null;
      this.assignedRoleCodes = [];
      this.originalAssignedRoleCodes = [];
      this.roleChanged = false;
    },
    
    handleClose() {
      this.currentUser = null;
      this.userSearchKeyword = '';
      this.filteredUserList = [...this.userList];
      this.assignedRoleCodes = [];
      this.originalAssignedRoleCodes = [];
      this.roleChanged = false;
    },
    
    // ========== 用户相关方法 ==========
    async fetchUserList() {
      this.userLoading = true
      try {
        const response = await getUserList({
            pageNum: 1,
            pageSize: 1000
        })
        
        if (response && response.code === 200) {
          // 根据返回结构处理数据
          if (response.data.content) {
            // 分页结构
            this.userList = response.data.content
          } else {
            this.userList = response.data.list || response.data || []
          }
          this.filteredUserList = [...this.userList]
        } else {
          this.$message.error(response?.message || '获取用户列表失败')
        }
      } catch (error) {
        console.error('获取用户列表出错:', error)
        this.$message.error('获取用户列表失败')
      } finally {
        this.userLoading = false
      }
    },

    filterUsers() {
      if (!this.userSearchKeyword) {
        this.filteredUserList = [...this.userList]
        return
      }
      
      const keyword = this.userSearchKeyword.toLowerCase()
      this.filteredUserList = this.userList.filter(user => 
        (user.username && user.username.toLowerCase().includes(keyword)) || 
        (user.realName && user.realName.toLowerCase().includes(keyword))
      )
    },

    handleUserSelect(user) {
      if (!user) return
      this.currentUser = user
      this.fetchUserRoles(user.id)
    },

    // ========== 角色相关方法 ==========
    async fetchRoleList() {
      this.roleLoading = true
      try {
        const response = await getRoleList()
        
        if (response && response.code === 200) {
          this.roleList = response.data || []
          // 准备角色数据
          this.allRoleOptions = this.roleList.map(role => ({
            id: Number(role.id),
            roleName: role.roleName,
            roleCode: role.roleCode,
            description: role.description
          }))
        } else {
          this.$message.error(response?.message || '获取角色列表失败')
        }
      } catch (error) {
        console.error('获取角色列表出错:', error)
        this.$message.error('获取角色列表失败')
      } finally {
        this.roleLoading = false
      }
    },

    async fetchUserRoles(userId) {
      try {
        const response = await getUserRoles(userId)
        
        if (response && response.code === 200) {
          // 处理返回的角色代码数组
          this.assignedRoleCodes = response.data || []
          this.originalAssignedRoleCodes = [...this.assignedRoleCodes]
          this.roleChanged = false
        } else {
          this.$message.error(response?.message || '获取用户角色失败')
        }
      } catch (error) {
        console.error('获取用户角色出错:', error)
        this.$message.error('获取用户角色失败')
      }
    },

    handleRoleChange() {
      // 检查是否有变化
      if (this.originalAssignedRoleCodes.length !== this.assignedRoleCodes.length) {
        this.roleChanged = true
        return
      }
      
      const sortedOriginal = [...this.originalAssignedRoleCodes].sort()
      const sortedCurrent = [...this.assignedRoleCodes].sort()
      
      for (let i = 0; i < sortedOriginal.length; i++) {
        if (sortedOriginal[i] !== sortedCurrent[i]) {
          this.roleChanged = true
          return
        }
      }
      
      this.roleChanged = false
    },

    async saveUserRoles() {
      if (!this.currentUser || !this.roleChanged) return
      
      this.savingRoles = true
      try {
        const response = await assignUserRoles(this.currentUser.id, {
          userId: this.currentUser.id,
          roleCodes: this.assignedRoleCodes
        })
        
        if (response && response.code === 200) {
          this.$message.success('用户角色分配成功')
          this.originalAssignedRoleCodes = [...this.assignedRoleCodes]
          this.roleChanged = false
        } else {
          this.$message.error(response?.message || '用户角色分配失败')
        }
      } catch (error) {
        console.error('保存用户角色出错:', error)
        this.$message.error('用户角色分配失败')
      } finally {
        this.savingRoles = false
      }
    }
  }
}
</script>

<style scoped>
.permission-content {
  display: flex;
  min-height: 500px;
}

.left-panel {
  width: 350px;
  padding-right: 15px;
  border-right: 1px solid #e6e6e6;
}

.middle-panel {
  width: 700px;
  padding: 0 15px;
}

.panel-title {
  font-size: 16px;
  color: #303133;
  margin-top: 0;
  margin-bottom: 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.current-selected {
  font-size: 14px;
  font-weight: normal;
}

.search-box {
  margin-bottom: 10px;
}

.empty-notice {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 200px;
  color: #909399;
}

.empty-notice i {
  font-size: 48px;
  margin-bottom: 10px;
}

.role-assignment-area {
  margin-top: 20px;
}

/* 角色选择区域样式 */
.role-selection {
  display: flex;
  flex-wrap: wrap;
  padding: 10px;
  background-color: #f5f7fa;
  border-radius: 4px;
  max-height: 350px;
  overflow-y: auto;
}

.role-checkbox {
  width: calc(50% - 10px);
  margin: 5px;
  background-color: #fff;
  padding: 8px 12px;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.3s;
}

.role-checkbox:hover {
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  transform: translateY(-2px);
}

.role-info {
  padding-left: 6px;
}

.role-name {
  font-weight: bold;
  color: #303133;
}

.role-desc {
  font-size: 12px;
  color: #909399;
  margin-top: 3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.action-buttons {
  margin-top: 20px;
  text-align: center;
}

/* 选中指示器样式 */
.selection-indicator {
  font-size: 16px;
  color: #409EFF;
}

/* ElementUI 组件样式重写 */
:deep(.el-checkbox__label) {
  padding-left: 6px;
}

:deep(.el-divider__text) {
  background-color: transparent;
  color: #606266;
  font-weight: 500;
}

:deep(.el-checkbox__input.is-checked+.el-checkbox__label) {
  color: #409EFF;
}

:deep(.el-checkbox__input.is-checked .el-checkbox__inner) {
  background-color: #409EFF;
  border-color: #409EFF;
}
</style> 